import React, { useEffect, useState } from "react";
// CHANGED: Import HashRouter instead of BrowserRouter
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";

import { auth } from "./firebaseConfig";

import NavBar from "./components/NavBar"; 
import CarListingPage from "./components/CarListingPage";
import CarDetailsPage from "./components/CarDetailsPage";
import InvestmentsPage from "./components/InvestmentsPage";
import ProfilePage from "./components/ProfilePage";
import LoginPage from "./components/LoginPage";
import RankingsPage from "./components/RankingsPage";
import AppointmentPage from "./components/AppointmentPage";
import RentPage from "./components/RentalPage.js";
import DallasHubPage from "./components/DallasHubPage";
import AdminPanel from "./components/AdminPanel/AdminPanel";
import DashboardMetrics from "./components/AdminPanel/DashboardMetrics";
import CarsManagement from "./components/AdminPanel/CarsManagement";
import UsersManagement from "./components/AdminPanel/UsersManagement";
import Approvals from "./components/AdminPanel/Approvals";
import DocumentsAlerts from "./components/AdminPanel/DocumentsAlerts";
import ReportsInsights from "./components/AdminPanel/ReportsInsights";
import AddCarForm from "./components/AdminPanel/AddCarForm";
import CreateListingPage from "./components/CreateListingPage";
import SingleListingPage from "./components/SingleListingPage";
import PrivateBatchPage from "./components/PrivateBatchPage";
import FleetPage from "./components/FleetPage";
import RequestsPage from "./components/RequestsPage";
import TransactionsDashboard from "./components/AdminPanel/TransactionsDashboard";
import CarsList from "./components/AdminPanel/CarsList"; 
import AdminPayments from "./components/AdminPanel/AdminPayments.js";
import UpdatePaymentMethod from "./components/UpdatePaymentMethod.js";
import PayoutCar from "./components/AdminPanel/PayoutCar.js";
import PrivateListingPage from "./components/PrivateListingPage.js";
import TermsOfService from "./components/TermsOfService.js";
import RentalPage from "./components/RentalPage.js";
import RentalProcessPage from "./components/RentalProcessPage"; 
import AdminRentals from './components/AdminPanel/AdminRentals.js';
import UserTripsManagementPage from './components/UserTripsManagementPage.jsx';
import FeedbackAndSupportPage from "./components/feedback.js";
import AdminFeedback from "./components/AdminPanel/AdminFeedback.js"; 
import ProtectedRoute from "./components/ProtectedRoute";
import SuperAdminDashboard from './components/AdminPanel/SuperAdminDashboard.js';
import CarDetailsForRent from "./components/CarDetailsRent.js";
import GiveUsYourCarForm from "./components/GiveUsYourCarForm.js";
import EditCarDetails from "./components/AdminPanel/EditCarDetails";
import HowItWorksPage from "./components/HowItWorksPage";
import HomePage from "./components/HomePage";
import RandomCarGeneratorPage from "./components/AdminPanel/RandomCarGeneratorPage";
import WhyCarsPage from "./components/WhyCarsPage";
import AboutPage from "./components/AboutPage.js";
import ContactUsPage from "./components/ContactUsPage.js";
import Footer from "./components/Footer.js";  // Import Footer
import BackgroundAdmin from "./components/AdminPanel/BackgroundAdmin.js";
import MobileLandingPage from "./components/MobileCarListingPage";

const stripePromise = loadStripe("pk_test_51Qfq8mG2dhqtrscWgXh7lKW7H9I1lL4xEi8zTkod2cJ4v8NxZsV2b0FLV0FCKAvzbhYumINzhyGqWb96yx7X6UVx00pnQGhN83");

function AppRoutes() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = window.innerWidth <= 768;

  useEffect(() => {
    // If a mobile user visits the marketplace page, redirect them to /mobile.
    if (isMobile && location.pathname === "/marketplace") {
      navigate("/mobile", { replace: true });
    }
  }, [isMobile, location, navigate]);

  return (
    <Routes>
      <Route path="/mobile" element={<MobileLandingPage />} />
      <Route path="/" element={<HomePage />} />
      <Route path="/contact" element={<ContactUsPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/marketplace" element={<CarListingPage />} />
      {/* Add any other routes as needed */}
    </Routes>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [currentPage, setCurrentPage] = useState("marketplace");

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  const showBackButton = !["marketplace", "investments", "settings", "profile"].includes(
    currentPage
  );

  return (
    <Elements stripe={stripePromise}>
      {/* CHANGED: Use HashRouter for stable refreshes */}
      <Router>
        <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
          <NavBar setCurrentPage={setCurrentPage} showBackButton={showBackButton} />
          
          {user ? (
            <Routes>
              {/* Mobile Landing Page Route available for authenticated users */}
              <Route path="/mobile" element={<MobileLandingPage />} />

              {/* Regular User Routes */}
              <Route path="/" element={<CarListingPage />} />
              <Route path="/marketplace" element={<CarListingPage />} />
              <Route path="/cars/:carID" element={<CarDetailsPage />} />
              <Route path="/investments" element={<InvestmentsPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/rankings" element={<RankingsPage />} />
              <Route path="/hub/dallas" element={<DallasHubPage />} />
              <Route path="/appointment" element={<AppointmentPage />} />
              <Route path="/settings" element={<UpdatePaymentMethod />} />
              <Route path="/private-listing" element={<PrivateListingPage />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/rent" element={<RentPage />} />
              <Route path="/create-listing" element={<CreateListingPage />} />
              <Route path="/create-single-listing" element={<SingleListingPage />} />
              <Route path="/RentalPage" element={<RentalPage />} />
              <Route path="/rent/:carID" element={<CarDetailsForRent />} />
              <Route path="/create-private-batch" element={<PrivateBatchPage />} />
              <Route path="/create-a-fleet" element={<FleetPage />} />
              <Route path="/requests" element={<RequestsPage />} />
              <Route path="/my-trips" element={<UserTripsManagementPage />} />
              <Route path="/feedback" element={<FeedbackAndSupportPage />} />
              <Route path="/rental/:carID" element={<RentalProcessPage />} />
              <Route path="/give-us-your-car" element={<GiveUsYourCarForm />} />
              <Route path="/why-cars" element={<WhyCarsPage />} />

              {/* Admin Panel Routes */}
              <Route
                path="/admin/*"
                element={
                  <ProtectedRoute>
                    <AdminPanel />
                  </ProtectedRoute>
                }
              >
                <Route index element={<DashboardMetrics />} />
                <Route path="dashboard" element={<DashboardMetrics />} />
                <Route path="cars" element={<CarsManagement />} />
                <Route path="add" element={<AddCarForm />} />
                <Route path="list" element={<CarsList />} />
                <Route path="transactions" element={<TransactionsDashboard />} />
                <Route path="payout-car" element={<PayoutCar />} />
                <Route path="payments" element={<AdminPayments />} />
                <Route path="admin-feedback" element={<AdminFeedback />} />
                <Route path="users" element={<UsersManagement />} />
                <Route path="approvals" element={<Approvals />} />
                <Route path="documents" element={<DocumentsAlerts />} />
                <Route path="reports" element={<ReportsInsights />} />
                <Route path="rentals" element={<AdminRentals />} />
                <Route path="super-dashboard" element={<SuperAdminDashboard />} />
                <Route path="edit-car-details" element={<EditCarDetails />} />
                <Route path="random-car-generator" element={<RandomCarGeneratorPage />} />
                <Route path="background" element={<BackgroundAdmin />} />
              </Route>
            </Routes>
          ) : (
            <Routes>
              {/* Mobile Landing Page Route available for unauthenticated users */}
              <Route path="/mobile" element={<MobileLandingPage />} />

              <Route path="/" element={<CarListingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/how-it-works" element={<HowItWorksPage />} />
              <Route path="/why-cars" element={<WhyCarsPage />} />
              <Route path="/marketplace" element={<CarListingPage />} />
              <Route path="/rent" element={<RentPage />} />
              <Route path="/RentalPage" element={<RentalPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactUsPage />} />
              {/* Redirect all other paths to login for non-authenticated users */}
              <Route path="/*" element={<LoginPage />} />
            </Routes>
          )}
          
          <Footer /> {/* Footer appears on every page */}
        </div>
      </Router>
    </Elements>
  );
}

export default App;
